import api from './api';
import { Product, ProductCategory } from '@/types';

export interface ProductFilters {
  category_id?: number;
  supplier_id?: number;
  search?: string;
  only_active?: boolean;
  only_low_stock?: boolean;
  product_type?: 'product' | 'service';
  skip?: number;
  limit?: number;
}

export interface ProductCreate {
  // null = borrar el código guardado (undefined no viaja y el backend lo deja como estaba)
  code?: string | null;
  barcode?: string | null;
  name: string;
  description?: string;
  category_id?: number;
  supplier_id?: number;
  cost_price?: number;
  sale_price?: number;  // Precio efectivo/transferencia
  list_price?: number;  // Precio tarjeta
  installment_price?: number;  // Precio crédito en cuotas
  current_stock?: number;
  minimum_stock?: number;
  track_stock?: boolean;
  expiration_date?: string;
  batch_number?: string;
  is_service?: boolean;
  applicable_species?: string;
  // Venta por ml
  sell_by_ml?: boolean;
  container_size_ml?: number;
  price_per_ml?: number;
  // Producto a granel
  is_bulk?: boolean;
  // Fraccionado
  source_product_id?: number;
  fraction_quantity?: number;
}

export interface PriceUpdateBulk {
  percentage?: number;
  items?: { product_id: number; new_price: number; reason?: string }[];
  category_id?: number;
  reason?: string;
}

export const productsService = {
  // Categorías
  async getCategories(): Promise<ProductCategory[]> {
    const response = await api.get<ProductCategory[]>('/products/categories');
    return response.data;
  },

  async createCategory(data: {
    name: string;
    description?: string;
    price_formula?: string;
    price_multiplier?: number;
    margin_percentage?: number;
    price_round_to?: number;
    list_price_multiplier?: number;
  }): Promise<ProductCategory> {
    const response = await api.post<ProductCategory>('/products/categories', data);
    return response.data;
  },

  async updateCategory(id: number, data: {
    name?: string;
    description?: string;
    is_active?: boolean;
    price_formula?: string;
    price_multiplier?: number;
    margin_percentage?: number;
    price_round_to?: number;
    list_price_multiplier?: number;
  }): Promise<ProductCategory> {
    const response = await api.put<ProductCategory>(`/products/categories/${id}`, data);
    return response.data;
  },

  async deleteCategory(id: number): Promise<void> {
    await api.delete(`/products/categories/${id}`);
  },

  // Productos
  async getProducts(filters?: ProductFilters): Promise<Product[]> {
    const response = await api.get<Product[]>('/products', { params: filters });
    return response.data;
  },

  async getProduct(id: number): Promise<Product> {
    const response = await api.get<Product>(`/products/${id}`);
    return response.data;
  },

  // Busca por código de barras (para el lector). Devuelve null si no existe.
  async getProductByBarcode(barcode: string): Promise<Product | null> {
    try {
      const response = await api.get<Product>(`/products/by-barcode/${encodeURIComponent(barcode)}`);
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  async createProduct(data: ProductCreate): Promise<Product> {
    const response = await api.post<Product>('/products', data);
    return response.data;
  },

  async updateProduct(
    id: number,
    data: Partial<ProductCreate>,
    options?: {
      cost_notes?: string;
      supplier?: string;
      quantity_purchased?: number;
    }
  ): Promise<Product> {
    const response = await api.put<Product>(`/products/${id}`, data, {
      params: options,
    });
    return response.data;
  },

  async updateStock(id: number, quantity: number): Promise<Product> {
    const response = await api.patch<Product>(`/products/${id}/stock`, null, {
      params: { quantity },
    });
    return response.data;
  },

  async bulkUpdatePrices(data: PriceUpdateBulk): Promise<{ message: string; updated_count: number }> {
    const response = await api.post('/products/prices/bulk-update', data);
    return response.data;
  },

  async bulkUpdatePricesByIds(data: {
    product_ids: number[];
    percentage: number;
    recalculate_from_cost?: boolean;
    reason?: string;
  }): Promise<{ updated_count: number; products_updated: number[] }> {
    const response = await api.post('/products/prices/bulk-update-by-ids', data);
    return response.data;
  },

  async recalculateListPrices(categoryId?: number): Promise<{
    message: string;
    updated_count: number;
    surcharge_percent: number;
  }> {
    const response = await api.post('/products/prices/recalculate-list-prices', null, {
      params: categoryId ? { category_id: categoryId } : undefined,
    });
    return response.data;
  },

  async deleteProduct(id: number): Promise<void> {
    await api.delete(`/products/${id}`);
  },
};
